import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  useToast,
  type BadgeTone,
} from '@/components/ui';
import { useMe, useSession } from '@/features/auth/session-context';
import { api, isApiError } from '@/lib/api';
import { whenLabel } from '@/pages/calendar/time';
import styles from './parity.module.css';
import { ROLES } from './staff-roles';

export interface Invite {
  id: string;
  email: string;
  fullName: string;
  roles: { role: string; branchCode: string | null }[];
  mmcNumber: string | null;
  status: 'pending' | 'expired' | 'accepted' | 'revoked';
  expiresAt: string;
  createdAt: string;
  acceptedAt: string | null;
}
interface Issued {
  invite: Invite;
  token: string;
  delivery: { status: string; reason: string };
}

const roleLabel = (r: string) => ROLES.find((x) => x.value === r)?.label ?? r;
const STATUS: Record<Invite['status'], { label: string; tone: BadgeTone }> = {
  pending: { label: 'Invited', tone: 'warning' },
  expired: { label: 'Expired', tone: 'neutral' },
  accepted: { label: 'Accepted', tone: 'success' },
  revoked: { label: 'Cancelled', tone: 'danger' },
};
const inviteLink = (token: string) =>
  `${window.location.origin}/accept-invite#${token}`;

function useInviteAction<V>(run: (v: V) => Promise<Issued | Invite>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'staff-invites'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
    },
  });
}

/**
 * RL-1034 invitations (prototype "Invite staff" and "Cancel invite"): list,
 * resend and cancel. The link is shown once after create/resend because no
 * email provider is connected; it is never stored or shown again.
 */
/** Refusals worth saying in words, with no code or Retry (RL-1793, #154). */
const INVITE_REFUSALS: Record<string, string> = {
  STAFF_EXISTS:
    'This email already has a staff account. Change their roles on the staff record instead.',
  INVITE_PENDING:
    'This person already has an invitation waiting. Resend or cancel it below.',
};

export function StaffInvitations() {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const canManage = hasPermission('staff:manage');
  const toast = useToast();
  const [issued, setIssued] = useState<Issued | null>(null);
  const q = useQuery({
    queryKey: ['admin', 'staff-invites'],
    queryFn: ({ signal }) =>
      api.get<Invite[]>('/admin/staff-invites', { signal }).then((r) => r.data),
  });
  const resend = useInviteAction((id: string) =>
    api.post<Issued>(`/admin/staff-invites/${id}/resend`).then((r) => r.data),
  );
  const revoke = useInviteAction((id: string) =>
    api.post<Invite>(`/admin/staff-invites/${id}/revoke`).then((r) => r.data),
  );
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  return (
    <Card title="Invitations" flush>
      <DataTable
        caption="Invitations"
        rows={q.data}
        rowKey={(i) => i.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={<EmptyState title="No invitations yet" />}
        columns={[
          {
            key: 'p',
            header: 'Person',
            render: (i) => (
              <>
                <strong>{i.fullName}</strong>
                <span className={styles.sub}>{i.email}</span>
              </>
            ),
          },
          {
            key: 'r',
            header: 'Role',
            render: (i) =>
              `${i.roles[0]?.role === 'locum' ? t('staffInvites.roles.locum') : roleLabel(i.roles[0]?.role ?? '')} · ${i.roles.map((r) => r.branchCode ?? 'all branches').join(', ')}`,
          },
          {
            key: 's',
            header: 'Status',
            render: (i) => (
              <Badge tone={STATUS[i.status].tone}>
                {STATUS[i.status].label}
              </Badge>
            ),
          },
          {
            key: 'e',
            header: 'Expires',
            render: (i) =>
              i.status === 'accepted' && i.acceptedAt
                ? `Accepted ${whenLabel(i.acceptedAt)}`
                : whenLabel(i.expiresAt),
          },
          {
            key: 'x',
            header: '',
            render: (i) =>
              canManage &&
              (i.status === 'pending' || i.status === 'expired') && (
                <div className={styles.rowActions}>
                  <Button
                    size="sm"
                    loading={resend.isPending && resend.variables === i.id}
                    onClick={() =>
                      void resend
                        .mutateAsync(i.id)
                        .then((r) => setIssued(r as Issued))
                        .catch(fail)
                    }
                  >
                    {i.status === 'expired' ? 'Send a new link' : 'Resend'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={revoke.isPending && revoke.variables === i.id}
                    onClick={() =>
                      void revoke
                        .mutateAsync(i.id)
                        .then(() =>
                          toast.show({
                            tone: 'success',
                            title: 'Invitation cancelled',
                          }),
                        )
                        .catch(fail)
                    }
                  >
                    Cancel invite
                  </Button>
                </div>
              ),
          },
        ]}
      />
      {issued && <LinkDialog issued={issued} onClose={() => setIssued(null)} />}
    </Card>
  );
}

export function InviteButton() {
  const { hasPermission } = useSession();
  const [open, setOpen] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);
  if (!hasPermission('staff:manage')) return null;
  return (
    <>
      <Button size="sm" variant="primary" onClick={() => setOpen(true)}>
        Invite staff
      </Button>
      {open && (
        <InviteDialog
          onClose={() => setOpen(false)}
          onIssued={(i) => {
            setOpen(false);
            setIssued(i);
          }}
        />
      )}
      {issued && <LinkDialog issued={issued} onClose={() => setIssued(null)} />}
    </>
  );
}

function InviteDialog({
  onClose,
  onIssued,
}: {
  onClose: () => void;
  onIssued: (i: Issued) => void;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const toast = useToast();
  const [v, setV] = useState({
    fullName: '',
    email: '',
    role: 'front_desk',
    branch: '',
    mmcNumber: '',
  });
  const role = ROLES.find((r) => r.value === v.role)!;
  const branchOptions = [
    ...me.branches.map((b) => ({ value: b.code, label: b.name })),
    ...(me.branches.length > 1 ? [{ value: '*', label: 'Both branches' }] : []),
  ];
  const create = useInviteAction(() =>
    api
      .post<Issued>('/admin/staff-invites', {
        fullName: v.fullName.trim(),
        email: v.email.trim(),
        role: v.role,
        branchCodes: role.allBranches
          ? []
          : v.branch === '*'
            ? me.branches.map((b) => b.code)
            : [v.branch],
        ...((v.role === 'doctor' || v.role === 'locum') && v.mmcNumber.trim()
          ? { mmcNumber: v.mmcNumber.trim() }
          : {}),
      })
      .then((r) => r.data),
  );
  const invalid =
    v.fullName.trim().length < 3 ||
    !/^\S+@\S+\.\S+$/.test(v.email.trim()) ||
    (!role.allBranches && !v.branch);
  return (
    <Dialog
      open
      title="Invite staff"
      onClose={onClose}
      dismissible={!create.isPending}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={invalid}
            onClick={() =>
              void create
                .mutateAsync(undefined)
                .then((r) => onIssued(r as Issued))
                .catch((err: unknown) =>
                  toast.show(
                    // RL-1793 (#154): an existing account or invitation is a
                    // plain refusal, without "Code: STAFF_EXISTS" or Retry.
                    isApiError(err) && INVITE_REFUSALS[err.code]
                      ? {
                          tone: 'error',
                          title: 'Not invited',
                          body: INVITE_REFUSALS[err.code],
                        }
                      : {
                          tone: 'error',
                          title: 'Not invited',
                          body: isApiError(err) ? err.message : undefined,
                          error: err,
                        },
                  ),
                )
            }
          >
            Create invitation
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field label="Full name">
          <Input
            value={v.fullName}
            maxLength={160}
            onChange={(e) => setV({ ...v, fullName: e.target.value })}
          />
        </Field>
        <Field label="Work email">
          <Input
            type="email"
            value={v.email}
            maxLength={254}
            onChange={(e) => setV({ ...v, email: e.target.value })}
          />
        </Field>
        <Field label="Role">
          <Select
            value={v.role}
            onChange={(e) => setV({ ...v, role: e.target.value })}
            options={ROLES.map((r) => ({
              value: r.value,
              label:
                r.value === 'locum' ? t('staffInvites.roles.locum') : r.label,
            }))}
          />
        </Field>
        {role.allBranches ? (
          <p className={styles.sub}>{role.label} covers every branch.</p>
        ) : (
          <Field label="Branch">
            <Select
              value={v.branch}
              placeholder="Choose a branch"
              onChange={(e) => setV({ ...v, branch: e.target.value })}
              options={branchOptions}
            />
          </Field>
        )}
        {(v.role === 'doctor' || v.role === 'locum') && (
          <Field label={t('staffInvites.mmcNumber')}>
            <Input
              value={v.mmcNumber}
              maxLength={32}
              placeholder="optional"
              onChange={(e) => setV({ ...v, mmcNumber: e.target.value })}
            />
          </Field>
        )}
        <p className={styles.notice}>
          Doctors must supply an MMC number before they can sign anything. The
          person chooses their own password from the invitation link; two-factor
          is set up at first sign-in where the role requires it.
        </p>
      </div>
    </Dialog>
  );
}

function LinkDialog({
  issued,
  onClose,
}: {
  issued: Issued;
  onClose: () => void;
}) {
  const toast = useToast();
  const link = inviteLink(issued.token);
  return (
    <Dialog
      open
      title={`Invitation for ${issued.invite.fullName}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className={styles.form}>
        <p className={styles.notice}>{issued.delivery.reason}</p>
        <Field label="Invitation link (shown once)">
          <Input readOnly value={link} />
        </Field>
        <Button
          size="sm"
          onClick={() =>
            void navigator.clipboard
              ?.writeText(link)
              .then(() => toast.show({ tone: 'success', title: 'Link copied' }))
          }
        >
          Copy link
        </Button>
        <p className={styles.sub}>
          It works once and expires {whenLabel(issued.invite.expiresAt)}. Send
          it only to {issued.invite.email}. Resend makes a new link and the old
          one stops working.
        </p>
      </div>
    </Dialog>
  );
}
